import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

// ---------------------------------------------------------------------------
// Shared building blocks for the (mock) MCP assistant demos — B2C + B2B.
// Pure presentation; no LLM, no network. Keeps the Swiss / high-contrast look.
// ---------------------------------------------------------------------------

// Assistant selector pill bar (Claude / ChatGPT / Gemini / Other).
export function AssistantBar({ assistants, active, onChange, testidPrefix = "mcp" }) {
  return (
    <div className="inline-grid grid-flow-col auto-cols-fr gap-1 p-0.5 rounded-full bg-[#FAFAFA] border border-black/5">
      {assistants.map((a) => (
        <button
          key={a.key}
          type="button"
          onClick={() => onChange(a.key)}
          data-testid={`${testidPrefix}-assistant-${a.key}`}
          className={`px-3.5 py-1.5 rounded-full text-[12px] transition-all whitespace-nowrap ${
            active === a.key
              ? "bg-[#0A0A0A] text-white"
              : "text-neutral-500 hover:text-[#0A0A0A]"
          }`}
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}

// Progressive "typing" text. Renders `text` character-by-character, then calls
// onDone. Purely cosmetic — the full string is known up front (it's mock copy).
export function TypingText({ text, speed = 12, onDone }) {
  const [count, setCount] = useState(0);
  const doneRef = useRef(false);

  useEffect(() => {
    setCount(0);
    doneRef.current = false;
  }, [text]);

  useEffect(() => {
    if (count >= text.length) {
      if (!doneRef.current) {
        doneRef.current = true;
        onDone?.();
      }
      return;
    }
    const id = setTimeout(() => setCount((c) => Math.min(text.length, c + 2)), speed);
    return () => clearTimeout(id);
  }, [count, text, speed, onDone]);

  const typing = count < text.length;
  return (
    <span>
      {text.slice(0, count)}
      {typing && (
        <span className="inline-block w-[6px] h-[14px] -mb-[2px] ml-0.5 bg-current opacity-60 animate-pulse" />
      )}
    </span>
  );
}

// A single chat bubble. role: "user" (right, dark) | "assistant" (left, light).
export function ChatBubble({ role = "assistant", children, testid }) {
  const isUser = role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testid}
      className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
        isUser
          ? "ml-auto bg-[#0A0A0A] text-white"
          : "mr-auto bg-white border border-black/10 text-neutral-800"
      }`}
    >
      {children}
    </motion.div>
  );
}

// A small "illustrative demo" chip — every mock panel is labelled with this so
// nothing reads as a verified fit fact.
export function DemoChip({ children = "Illustrative demo", testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-100 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] text-amber-700"
    >
      {children}
    </span>
  );
}
